import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import useImpersonateContext from 'src/hooks/useImpersonateContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
    { bg: '#B5D4F4', color: '#0C447C' },
    { bg: '#9FE1CB', color: '#085041' },
    { bg: '#F5C4B3', color: '#712B13' },
    { bg: '#CECBF6', color: '#3C3489' },
    { bg: '#C0DD97', color: '#27500A' },
];

function resolveAvatarColor(id: string) {
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

const SEARCH_SX = {
    mb: 2,
    '& .MuiInputBase-input': { pt: '12px', pb: '12px', fontSize: 14 },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function ImpersonateDialog() {
    const {
        isOpen,
        onClose,
        step,
        customersLoading,
        filteredCustomers,
        selectedCustomer,
        onSelectCustomer,
        onNextStep,
        onChangeCustomer,
        search,
        onSearch,
        usersLoading,
        selectedUser,
        onSelectUser,
        confirming,
        onConfirm,
        searchUser,
        onSearchUser,
        filteredUsers,
    } = useImpersonateContext();

    // Customers with logo first, then the rest alphabetically
    const sortedCustomers = [...filteredCustomers].sort((a, b) => {
        const aHasLogo = !!a.imageUrl;
        const bHasLogo = !!b.imageUrl;
        if (aHasLogo && !bHasLogo) return -1;
        if (!aHasLogo && bHasLogo) return 1;
        return a.name.localeCompare(b.name);
    });

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, width: 620, display: 'flex', flexDirection: 'column', height: 680 } }}
        >
            {/* ── Header ──────────────────────────────────────────────────────── */}
            <DialogTitle sx={{ p: 0, flexShrink: 0 }}>
                <Box
                    sx={{
                        bgcolor: 'primary.main',
                        borderRadius: '16px 16px 0 0',
                        px: 2.5,
                        py: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="h1" fontWeight={700} sx={{ color: 'white' }}>
                        Show dashboard as user
                    </Typography>
                    <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            {/* ── Stepper fisso ────────────────────────────────────────────────── */}
            <Box sx={{ px: 3, pt: 2.5, pb: 0, flexShrink: 0 }}>
                <Stepper activeStep={step} alternativeLabel>
                    <Step completed={step > 0}>
                        <StepLabel>Select customer</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Select user</StepLabel>
                    </Step>
                </Stepper>
            </Box>

            {/* ── Sticky search bar ────────────────────────────────────────────── */}
            <Box sx={{ px: 3, pt: 2, flexShrink: 0 }}>
                {step === 0 && (
                    <TextField
                        fullWidth
                        size="small"
                        variant="filled"
                        placeholder="Search customers…"
                        value={search}
                        onChange={(e) => onSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mt: '0px !important', alignItems: 'center' }}>
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={SEARCH_SX}
                    />
                )}

                {step === 1 && (
                    <>
                        <TextField
                            fullWidth
                            size="small"
                            variant="filled"
                            placeholder="Search users…"
                            value={searchUser}
                            onChange={(e) => onSearchUser(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ mt: '0px !important', alignItems: 'center' }}>
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={SEARCH_SX}
                        />

                        {/* Selected customer banner */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1,
                                pl: '12px',
                                mb: 2,
                                bgcolor: 'action.hover',
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            {selectedCustomer?.imageUrl ? (
                                <Avatar
                                    src={selectedCustomer.imageUrl}
                                    variant="rounded"
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        '& img': { objectFit: 'contain' },
                                    }}
                                />
                            ) : (
                                <BusinessIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                            )}
                            <Typography variant="body2" color="text.secondary">
                                Customer:
                            </Typography>
                            <Typography variant="body1">
                                {selectedCustomer?.name}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>

            {/* ── Scrollable content area ──────────────────────────────────────── */}
            <DialogContent sx={{ pt: 0, pb: 2, px: 3, overflow: 'auto', flexGrow: 1 }}>

                {/* ── Step 0: customer grid ────────────────────────────────────── */}
                {step === 0 && (
                    <>
                        {customersLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <Grid container spacing={1}>
                                {sortedCustomers.map((c) => {
                                    const isSelected = selectedCustomer?.id === c.id;
                                    return (
                                        <Grid item xs={4} key={c.id}>
                                            <Box
                                                onClick={() => onSelectCustomer(c)}
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: isSelected ? 'info.main' : 'divider',
                                                    borderRadius: 1.5,
                                                    p: 2,
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    bgcolor: isSelected ? 'info.lighter' : 'background.paper',
                                                    transition: 'border-color 0.15s, background-color 0.15s',
                                                    '&:hover': {
                                                        borderColor: 'info.light',
                                                        bgcolor: isSelected ? 'info.lighter' : 'action.hover',
                                                    },
                                                }}
                                            >
                                                {c.imageUrl ? (
                                                    <Avatar
                                                        src={c.imageUrl}
                                                        variant="rounded"
                                                        sx={{
                                                            width: 44,
                                                            height: 44,
                                                            mx: 'auto',
                                                            mb: 1,
                                                            '& img': { objectFit: 'contain' },
                                                        }}
                                                    />
                                                ) : (
                                                    <Avatar
                                                        variant="rounded"
                                                        sx={{
                                                            width: 44,
                                                            height: 44,
                                                            mx: 'auto',
                                                            mb: 1,
                                                            bgcolor: 'grey.200',
                                                            color: 'text.secondary',
                                                            fontSize: 14,
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {c.name.slice(0, 2).toUpperCase()}
                                                    </Avatar>
                                                )}
                                                <Typography variant="body2" fontWeight={500} noWrap display="block">
                                                    {c.name}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </>
                )}

                {/* ── Step 1: user list ────────────────────────────────────────── */}
                {step === 1 && (
                    <>
                        {usersLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {filteredUsers.map((u) => {
                                    const colors = resolveAvatarColor(u.id);
                                    const isSelected = selectedUser?.id === u.id;
                                    const initials = `${u.name?.[0] ?? ''}${u.surname?.[0] ?? ''}`.toUpperCase() || '??';
                                    const fullName = `${u.name ?? ''} ${u.surname ?? ''}`.trim();
                                    return (
                                        <Box
                                            key={u.id}
                                            onClick={() => onSelectUser(u)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: isSelected ? 'info.main' : 'divider',
                                                borderRadius: 1.5,
                                                cursor: 'pointer',
                                                bgcolor: isSelected ? 'info.lighter' : 'background.paper',
                                                transition: 'border-color 0.15s, background-color 0.15s',
                                                '&:hover': {
                                                    borderColor: 'info.light',
                                                    bgcolor: isSelected ? 'info.lighter' : 'action.hover',
                                                },
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    bgcolor: colors.bg,
                                                    color: colors.color,
                                                    fontSize: 15,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {initials}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" display="block" sx={{ textTransform: 'capitalize' }}>
                                                    {fullName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" display="block" noWrap sx={{ textTransform: 'capitalize' }}>
                                                    {u.role}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <Divider />

            {/* ── Footer ──────────────────────────────────────────────────────── */}
            <DialogActions sx={{ px: 2.5, py: 1.5, flexShrink: 0 }}>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>

                {step === 1 && (
                    <Button variant="secondary" onClick={onChangeCustomer}>
                        Back
                    </Button>
                )}

                {step === 0 && (
                    <Button
                        variant="secondary"
                        disabled={!selectedCustomer}
                        onClick={onNextStep}
                    >
                        Next
                    </Button>
                )}

                {step === 1 && (
                    <Button
                        variant="secondary"
                        disabled={!selectedUser || confirming}
                        onClick={onConfirm}
                        startIcon={confirming ? <CircularProgress size={14} color="inherit" /> : undefined}
                    >
                        Show dashboard
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}